import { FormLeavingListener } from '@exam-management/components';
import {
  useSaveInDevelopmentQuestionMutation,
  GetQuestionCreationTaskByIdDocument,
} from '@exam-management/graphql';
import type {
  QBankStructureLevelItemWithLevel,
  QBankStructureLevel,
} from '@exam-management/qbs-provider';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import type { PropsWithChildren } from '@shared/utils';
import { useEffect, useMemo, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useSingleQuestionDetails } from '../../providers';
import { parseQuestionFormDataForApi } from '../single-question-creation-form';

import {
  type SingleQuestionCreationEditFormInput,
  getSingleQuestionCreationEditSchema,
} from './single-question-creation.schema';
import { getQuestionDefaultValues } from './utils';

interface QuestionCreationFormProps extends PropsWithChildren {
  qbsLevels: QBankStructureLevel[];
  qbsLevelItems: QBankStructureLevelItemWithLevel[];
  taskId: string;
}

export default function SingleQuestionCreationEditForm(props: Readonly<QuestionCreationFormProps>) {
  const { children, qbsLevels, qbsLevelItems } = props;

  const { task } = useQuestionCreationTaskDetails();
  const { showSnackbar } = useSnackbar();
  const { singleQuestion } = useSingleQuestionDetails();

  const SingleQuestionCreationEditSchema = useMemo(
    () => getSingleQuestionCreationEditSchema(qbsLevels),
    [qbsLevels],
  );

  const formContext = useForm<SingleQuestionCreationEditFormInput>({
    resolver: zodResolver(SingleQuestionCreationEditSchema),
  });

  const handleResetForm = useCallback(async () => {
    const defaultValues = await getQuestionDefaultValues(singleQuestion, qbsLevels, qbsLevelItems);
    formContext.reset(defaultValues as SingleQuestionCreationEditFormInput);
  }, [singleQuestion, qbsLevels, qbsLevelItems, formContext]);

  useEffect(() => {
    handleResetForm();
  }, [handleResetForm]);

  const [saveInDevelopmentQuestion] = useSaveInDevelopmentQuestionMutation();

  const handleSingleQuestionCreationEditFormSubmit = async (
    formData: SingleQuestionCreationEditFormInput,
  ) => {
    const qBankStructureLevelItemId = formData.location.SUB_TOPIC?.value as string;
    const payload = parseQuestionFormDataForApi(formData, parseContentForApi);

    const result = await saveInDevelopmentQuestion({
      variables: {
        input: {
          id: singleQuestion.id,
          data: payload,
          qBankStructureLevelItemId,
        },
        taskId: task.id,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQuestionCreationTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId: task.id,
            },
          },
        },
      ],
      onCompleted: () => {
        showSnackbar({
          message: 'Question has been saved',
          variant: 'success',
        });
      },
    });

    if (result.errors) throw result.errors;
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleSingleQuestionCreationEditFormSubmit)}>
        {children}
      </Form>
      <FormLeavingListener onSubmit={handleSingleQuestionCreationEditFormSubmit} />
    </FormProvider>
  );
}
